import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  RelativeTime,
  GatePeek,
  SMART_ANALYSIS_FEATURE_KEY,
  isApplicantWithinNewWindow,
  cn,
  PersonListCard,
  PersonListCardSummary,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  MatchQualityBadge,
  FIT_ANALYSIS_COPY,
  personDescriptionFromProfile,
  applicantCardToPersonModel,
  openToWorkTitleBadge,
} from '@yeshuman/sdk'
import { forwardRef, type ReactNode } from 'react'
import {
  MoreHorizontal,
  CalendarClock,
  Clock3,
  MessageSquare,
  Gavel,
  ArrowRight,
  LockKeyhole,
  Quote,
} from 'lucide-react'
import type { ApplicantCard as ApplicantCardModel, Stage } from '../../hooks/use-opportunity-applicants'
import { AnalysisRetryButton } from '../search/analysis-retry-button'

export type ApplicantCardDensity = 'compact' | 'rich'

export type ApplicantCardCommitment = 'applied' | 'invited'

export type ApplicantScoresGate = {
  allowed: boolean
  featureLabel: string
  planName: string
  upgradePath: string
}

export type ApplicantAnalysisRetry = {
  onRetry: (applicationId: string) => void
  pendingIds: ReadonlySet<string>
  stuckIds: ReadonlySet<string>
  mutatingId: string | null
}

interface ApplicantCardProps {
  applicant: ApplicantCardModel
  stages: Stage[]
  onOpen: () => void
  onMoveStage: (stageSlug: string) => void
  onRecordDecision: (decision: 'hired' | 'rejected' | 'offer') => void
  onOpenThread?: () => void
  isOverlay?: boolean
  dragHandleProps?: Record<string, unknown>
  className?: string
  density?: ApplicantCardDensity
  commitment?: ApplicantCardCommitment
  rank?: number
  scoresGate?: ApplicantScoresGate
  analysisRetry?: ApplicantAnalysisRetry
}

function isNewApplicant(applicant: ApplicantCardModel): boolean {
  if (applicant.status !== 'applied') return false
  return isApplicantWithinNewWindow(applicant.applied_at)
}

function resolveCommitment(
  applicant: ApplicantCardModel,
  commitment?: ApplicantCardCommitment,
): ApplicantCardCommitment {
  if (commitment) return commitment
  return applicant.status === 'invited' ? 'invited' : 'applied'
}

function ApplicantActionsMenu({
  applicant,
  stages,
  onMoveStage,
  onRecordDecision,
  onOpenThread,
  commitment,
}: {
  applicant: ApplicantCardModel
  stages: Stage[]
  onMoveStage: (stageSlug: string) => void
  onRecordDecision: (decision: 'hired' | 'rejected' | 'offer') => void
  onOpenThread?: () => void
  commitment: ApplicantCardCommitment
}) {
  const currentSlug = applicant.current_stage?.slug
  const canMoveStage = commitment !== 'invited'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          aria-label="Applicant actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canMoveStage ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ArrowRight className="h-4 w-4" />
              Move to…
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuLabel>Stages</DropdownMenuLabel>
              {stages.map((stage) => (
                <DropdownMenuItem
                  key={stage.slug}
                  disabled={stage.slug === currentSlug}
                  onClick={() => onMoveStage(stage.slug)}
                >
                  {stage.name}
                  {stage.is_terminal ? (
                    <Badge variant="outline" className="ml-auto text-xs">
                      Terminal
                    </Badge>
                  ) : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : null}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Gavel className="h-4 w-4" />
            Record decision
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onRecordDecision('hired')}>Hired</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onRecordDecision('offer')}>
              Extend offer
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onRecordDecision('rejected')}
              className="text-destructive focus:text-destructive"
            >
              Reject
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {applicant.has_thread && onOpenThread ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onOpenThread}>
              <MessageSquare className="h-4 w-4" />
              Open thread
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function LockedScoreBadge({ scoresGate }: { scoresGate: ApplicantScoresGate }) {
  return (
    <GatePeek
      allowed={false}
      featureKey={SMART_ANALYSIS_FEATURE_KEY}
      featureLabel={scoresGate.featureLabel}
      planName={scoresGate.planName}
      upgradePath={scoresGate.upgradePath}
      description="Run ranked analysis to compare candidates against this role."
    >
      <Button type="button" variant="outline" size="sm">
        <LockKeyhole className="h-3 w-3" aria-hidden />
        Score
      </Button>
    </GatePeek>
  )
}

export const ApplicantCard = forwardRef<HTMLDivElement, ApplicantCardProps>(
  (
    {
      applicant,
      stages,
      onOpen,
      onMoveStage,
      onRecordDecision,
      onOpenThread,
      isOverlay = false,
      dragHandleProps,
      className,
      density = 'compact',
      commitment: commitmentProp,
      rank,
      scoresGate,
      analysisRetry,
    },
    ref,
  ) => {
    const commitment = resolveCommitment(applicant, commitmentProp)
    const showLockedScore = scoresGate != null && !scoresGate.allowed
    const showMatchQuality = !showLockedScore && typeof applicant.match_score === 'number'
    const scorePending = Boolean(analysisRetry?.pendingIds.has(applicant.id))
    const scoreStuck = Boolean(analysisRetry?.stuckIds.has(applicant.id))
    const showScoreRetry =
      !showLockedScore &&
      analysisRetry != null &&
      !scorePending &&
      (!showMatchQuality || scoreStuck)
    const timePrefix = commitment === 'invited' ? 'Invited' : 'Applied'

    const pipelineMeta: ReactNode = (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
        {applicant.source ? (
          <Badge variant="secondarySoft" className="text-xs font-normal uppercase tracking-wide">
            {applicant.source}
          </Badge>
        ) : null}
        {showLockedScore && scoresGate ? <LockedScoreBadge scoresGate={scoresGate} /> : null}
        {showMatchQuality ? (
          <MatchQualityBadge score={applicant.match_score} className="text-xs" />
        ) : null}
        {scorePending ? (
          <span className="text-xs text-muted-foreground" role="status" aria-live="polite">
            {FIT_ANALYSIS_COPY.employerPending}
          </span>
        ) : null}
        {scoreStuck ? (
          <span className="text-xs text-muted-foreground" role="status" aria-live="polite">
            {FIT_ANALYSIS_COPY.stuck}
          </span>
        ) : null}
        {showScoreRetry && analysisRetry ? (
          <AnalysisRetryButton
            mode="try"
            onClick={() => analysisRetry.onRetry(applicant.id)}
            pending={analysisRetry.mutatingId === applicant.id}
          />
        ) : null}
        {typeof applicant.days_in_stage === 'number' && commitment !== 'invited' ? (
          <span className="inline-flex items-center gap-1">
            <Clock3 className="h-3.5 w-3.5" />
            <span className="tabular-nums">{applicant.days_in_stage}d</span> in stage
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <CalendarClock className="h-3.5 w-3.5" />
          <RelativeTime value={applicant.applied_at} prefix={timePrefix} />
        </span>
        {applicant.has_cover_letter ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex text-muted-foreground" aria-label="Has cover letter">
                <Quote className="h-3.5 w-3.5" aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent>Has cover letter</TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    )

    const rankAside =
      typeof rank === 'number' ? (
        showLockedScore && scoresGate ? (
          <LockedScoreBadge scoresGate={scoresGate} />
        ) : (
          <Badge variant="default" className="tabular-nums">
            #{rank}
          </Badge>
        )
      ) : null

    return (
      <PersonListCard
        ref={ref}
        variant="pipeline"
        className={cn('cursor-pointer', commitment === 'invited' && 'border-dashed', className)}
        person={applicantCardToPersonModel(applicant)}
        agentHighlight={`application:${applicant.id}`}
        onOpen={onOpen}
        openAriaLabel={`Open applicant ${applicant.profile.name}`}
        isOverlay={isOverlay}
        surfaceProps={dragHandleProps}
        titleBadge={
          isNewApplicant(applicant)
            ? { label: 'new', variant: 'defaultSoft' }
            : openToWorkTitleBadge(applicant.profile.open_to_work)
        }
        headerAside={rankAside}
        menu={
          <ApplicantActionsMenu
            applicant={applicant}
            stages={stages}
            onMoveStage={onMoveStage}
            onRecordDecision={onRecordDecision}
            onOpenThread={onOpenThread}
            commitment={commitment}
          />
        }
        body={
          density === 'rich' ? (
            <PersonListCardSummary
              description={personDescriptionFromProfile(applicant.profile)}
              skills={applicant.profile.top_skills}
              metadata={pipelineMeta}
            />
          ) : (
            pipelineMeta
          )
        }
      />
    )
  },
)

ApplicantCard.displayName = 'ApplicantCard'
